import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import Layout from './layout';
import Login from './auth/login';
import { FakeSOSocket, SafeDatabaseUser } from '../types/types';
import { LoginProvider } from '../contexts/LoginContext';
import useLoginContext from '../hooks/useLoginContext';
import UserContext from '../contexts/UserContext';
import QuestionPage from './main/questionPage';
import TagPage from './main/tagPage';
import NewQuestionPage from './main/newQuestion';
import NewAnswerPage from './main/newAnswer';
import AnswerPage from './main/answerPage';
import MessagingPage from './main/messagingPage';
import DirectMessage from './main/directMessage';
import Signup from './auth/signup';
import UsersListPage from './main/usersListPage';
import ProfilePage from './main/profilePage';
import ProfileSettings from './profileSettings';
import AllGamesPage from './main/games/allGamesPage';
import GamePage from './main/games/gamePage';
import MyInvitationsPage from './main/games/MyInvitationsPage';
import AdminDashboard from './admin/Dashboard';
import TwoFactorPromptWrapper from './auth/2fa/TwoFactorPromptWrapper';
import ActivityFeed from './main/activity';
import GameInvitationNotification from './main/games/GameInvitationNotification';
import NotificationSetup from './main/NotificationSetup';
import Toaster from './ui/toaster';
import LeaderboardPage from './main/leaderboardPage';
import { refreshUserData } from '../services/userService';

const LoadingPage = () => (
  <div className='flex items-center justify-center h-screen'>
    <div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary'></div>
  </div>
);

// Handle game invitations
const GameInvitationRedirect: React.FC = () => {
  const { gameID } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useLoginContext();

  useEffect(() => {
    // Once user is loaded, redirect to the appropriate location
    if (!loading) {
      if (user) {
        // If user is logged in, redirect to the game
        navigate(`/games/${gameID}${location.search}`);
      } else {
        // If user is not logged in, redirect to login with return URL
        const returnUrl = `/games/${gameID}${location.search}`;
        navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
      }
    }
  }, [loading, user, gameID, location.search, navigate]);

  return (
    <div className='flex items-center justify-center h-screen'>
      <div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary'></div>
    </div>
  );
};

interface ProtectedRouteProps {
  socket: FakeSOSocket | null;
  children: JSX.Element;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ socket, children }) => {
  const { user, loading, setUser } = useLoginContext();

  // Refresh user data on route mount to ensure we have latest data
  useEffect(() => {
    if (user && user.username) {
      // Refresh user data when route is mounted
      refreshUserData(user.username)
        .then(refreshedData => {
          // Only update if there's a change - particularly for admin status
          if (refreshedData.isAdmin !== user.isAdmin) {
            setUser(refreshedData);
          }
        })
        .catch(error => {
          // silently handle error
        });
    }
  }, [user, setUser]);

  if (loading) {
    return <LoadingPage />;
  }

  if (!user || !socket) {
    return <Navigate to='/login' />;
  }

  // Function to update user data
  const updateUser = (updateData: Partial<SafeDatabaseUser>) => {
    // Don't recreate the entire user object for just updating one property
    // This will prevent excessive re-renders
    if (user) {
      // Create updated user with new data
      const updatedUser = { ...user, ...updateData };

      // Update localStorage
      localStorage.setItem('fakeSO_user', JSON.stringify(updatedUser));

      // Update cookie
      document.cookie = `fakeSO_user=${encodeURIComponent(JSON.stringify(updatedUser))}; path=/; max-age=${7 * 24 * 60 * 60}`;

      // Also update session storage if it exists
      const currentUserString = sessionStorage.getItem('currentUser');
      if (currentUserString) {
        const currentUser = JSON.parse(currentUserString);
        const newUserData = { ...currentUser, ...updateData };
        sessionStorage.setItem('currentUser', JSON.stringify(newUserData));
      }
    }
  };

  return (
    <UserContext.Provider value={{ user, socket, updateUser }}>
      <TwoFactorPromptWrapper>
        {children}
        <NotificationSetup />
        <GameInvitationNotification />
        <Toaster />
      </TwoFactorPromptWrapper>
    </UserContext.Provider>
  );
};

const PublicOnlyRoute: React.FC<{ children: JSX.Element }> = ({ children }) => {
  const { user, loading } = useLoginContext();

  if (loading) {
    return <LoadingPage />;
  }

  if (user) {
    return <Navigate to='/home' />;
  }

  return children;
};

/**
 * Represents the main component of the application.
 * It manages the state for search terms and the main title.
 */
const SyntaxError = ({ socket }: { socket: FakeSOSocket | null }) => (
  <LoginProvider>
    <Routes>
      {/* Game Invitation Route - needs special handling */}
      <Route path='/games/join/:gameID' element={<GameInvitationRedirect />} />

      {/* Public Routes */}
      <Route
        path='/'
        element={
          <PublicOnlyRoute>
            <Login />
          </PublicOnlyRoute>
        }
      />
      <Route
        path='/login'
        element={
          <PublicOnlyRoute>
            <Login />
          </PublicOnlyRoute>
        }
      />
      <Route
        path='/signup'
        element={
          <PublicOnlyRoute>
            <Signup />
          </PublicOnlyRoute>
        }
      />
      <Route path='/index.html' element={<Navigate to='/' replace />} />

      {/* Protected Routes */}
      <Route
        element={
          <ProtectedRoute socket={socket}>
            <Layout />
          </ProtectedRoute>
        }>
        <Route path='/home' element={<QuestionPage />} />
        <Route path='tags' element={<TagPage />} />
        <Route path='/messaging' element={<MessagingPage />} />
        <Route path='/messaging/direct-message' element={<DirectMessage />} />
        <Route path='/question/:qid' element={<AnswerPage />} />
        <Route path='/new/question' element={<NewQuestionPage />} />
        <Route path='/new/answer/:qid' element={<NewAnswerPage />} />
        <Route path='/users' element={<UsersListPage />} />
        <Route path='/user/:username' element={<ProfilePage />} />
        <Route path='/user/:username/settings' element={<ProfileSettings />} />
        <Route path='/games' element={<AllGamesPage />} />
        <Route path='/games/:gameID' element={<GamePage />} />
        <Route path='/games/invitations' element={<MyInvitationsPage />} />
        <Route path='/admin' element={<AdminDashboard />} />
        <Route path='/activity' element={<ActivityFeed />} />
        <Route path='/leaderboard' element={<LeaderboardPage />} />
      </Route>
    </Routes>
  </LoginProvider>
);

export default SyntaxError;
